import { useQuery } from '@tanstack/react-query';
import { Tag, type Column } from '@sparkery/ui';
import type { OrderRow, RangeKey, ReportResponse } from '@sparkery/contracts';
import { apiGet } from './api.ts';

export interface RangeSelection {
  range: RangeKey;
  from?: string;
  to?: string;
  /**
   * Sparkery staff only, and only a hint: the server pins a partner user to their own
   * partner whatever this says.
   */
  partnerId?: string;
}

/**
 * The one query both Dashboard and Reporting use.
 *
 * Sharing it means the two views cannot disagree about a period, and switching between
 * them at the same range is served from cache rather than re-fetched.
 */
export function useReport(selection: RangeSelection) {
  const params = new URLSearchParams({ range: selection.range });
  if (selection.from) params.set('from', selection.from);
  if (selection.to) params.set('to', selection.to);
  if (selection.partnerId) params.set('partnerId', selection.partnerId);

  return useQuery({
    queryKey: [
      'report',
      selection.range,
      selection.from ?? '',
      selection.to ?? '',
      selection.partnerId ?? '',
    ],
    queryFn: ({ signal }) => apiGet<ReportResponse>(`/reporting?${params.toString()}`, signal),
  });
}

export const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

/**
 * The range in a sentence, where RANGE_OPTIONS gives it on a control.
 *
 * Two spellings of one mapping — the segmented control's label and the prose one —
 * which were an options array and a nested ternary in a view.
 */
export const RANGE_LABEL: Record<RangeKey, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  custom: 'Selected period',
};

/**
 * Order state → tag tone, and code status → tag tone.
 *
 * Each was declared twice, in two views showing the same data, so the colour semantics
 * could drift between screens.
 */
export const ORDER_STATE_TONE = {
  confirmed: 'confirmed',
  pending: 'pending',
  refunded: 'refunded',
  cancelled: 'neutral',
} as const;

export const CODE_STATUS_TONE = {
  active: 'confirmed',
  scheduled: 'pending',
  expired: 'neutral',
  used: 'refunded',
} as const;

/** "confirmed" → "Confirmed". Was defined identically in three views. */
export function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * "1 link" / "3 links".
 *
 * One helper existed, in one view, while six other places wrote the same `=== 1` test
 * inline — including one that spelled it as a full if/else over two whole sentences.
 */
export function plural(count: number, word: string): string {
  return count === 1 ? `${count} ${word}` : `${count} ${word}s`;
}

/** Money arrives as a plain string; the symbol is the portal's single currency. */
export function money(amount: string): string {
  const value = Number(amount);
  return `£${Number.isFinite(value) ? value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : amount}`;
}

/** Whole money, for headline tiles where pence are noise. */
export function moneyShort(amount: string): string {
  const value = Number(amount);
  return `£${Number.isFinite(value) ? Math.round(value).toLocaleString('en-GB') : amount}`;
}

/**
 * `12.00` → `12`, `12.50` → `12.5`.
 *
 * The rate is stored to two places because it can be fractional; showing "at 12.00%"
 * beside a headline figure spends four characters saying nothing.
 */
export function ratePercent(rate: string): string {
  const value = Number(rate);
  return Number.isFinite(value) ? String(value) : rate;
}

/**
 * Drops the empty hours at each end of a by-hour series.
 *
 * The server returns all twenty-four so the shape of the day is never in question, but
 * a shop that trades 08:00–20:00 draws thirteen bars and eleven gaps, and the gaps
 * squeeze the bars into slivers. Interior zeros stay: a dead hour between two busy ones
 * is the thing worth seeing.
 *
 * Not applied to a by-day series — a day with no sales in the middle of a week is the
 * same information, and the ends of the range are the range the person asked for.
 */
export function trimEmptyEnds<T extends { value: number }>(bars: T[]): T[] {
  let first = bars.findIndex((bar) => bar.value > 0);
  if (first === -1) return bars;
  let last = bars.length - 1;
  while (last > first && bars[last]!.value === 0) last -= 1;
  // One bar alone reads as a bug rather than a chart, so keep an hour either side.
  first = Math.max(0, first - 1);
  last = Math.min(bars.length - 1, last + 1);
  return bars.slice(first, last + 1);
}

/**
 * "Peak 15:00 — £650", the line the prototype puts opposite the chart's title.
 *
 * `hourly` turns the server's bare "15" into "15:00". The axis can afford two
 * characters; a sentence cannot — "Peak 15" reads as a quantity.
 */
export function peakOf(
  bars: { label: string; value: number; formatted: string }[],
  options: { hourly?: boolean } = {},
): string | null {
  const peak = bars.reduce<{ label: string; value: number; formatted: string } | null>(
    (best, bar) => (best === null || bar.value > best.value ? bar : best),
    null,
  );
  if (!peak || peak.value <= 0) return null;
  const label = options.hourly ? `${peak.label}:00` : peak.label;
  return `Peak ${label} — ${peak.formatted}`;
}

/**
 * A conversion rate, or an em dash.
 *
 * A dash rather than "0.0%" when traffic has not synced: the denominator is unknown,
 * and on a fixed-fee dashboard this is the HEADLINE figure, so a confident zero would be
 * the most misleading number on the screen.
 *
 * And "<0.1" rather than "0.0" for a rate that is real but tiny. Rounding a genuine
 * 0.03% down to a flat zero asserts that nobody who visited bought anything, on a screen
 * that is simultaneously listing the orders they placed.
 */
export function conversionText(rate: number | null | undefined): string {
  if (rate === undefined || rate === null) return '—';
  if (rate === 0) return '0';
  if (rate < 0.1) return '<0.1';
  return rate.toFixed(1);
}

/** "Fri, 31 July 2026" — the dashboard's subtitle is the day, not the account name. */
export function longDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * The referral-orders table, for Dashboard and Reporting.
 *
 * Both had their own copy of these seven columns — identical, including the comment
 * about dropping commission for a fixed-fee partner, restated in both. Two copies of a
 * table definition is two places for a column to be added to one screen and not the
 * other.
 */
export function orderColumns(options: {
  showsCommission: boolean;
  range: RangeKey;
}): Column<OrderRow>[] {
  return [
    { id: 'name', header: 'Order', cell: (row) => row.name },
    { id: 'customer', header: 'Customer', cell: (row) => row.customerLabel ?? '—' },
    { id: 'product', header: 'Product', secondary: true, cell: (row) => row.product },
    { id: 'sale', header: 'Sale', numeric: true, cell: (row) => money(row.sale.amount) },
    // Dropped entirely for a fixed-fee partner rather than shown as a column of zeros,
    // which would invite the question of what they had done wrong.
    ...(options.showsCommission
      ? [
          {
            id: 'commission',
            header: 'Commission',
            numeric: true,
            cell: (row: OrderRow) => money(row.commission.amount),
          },
        ]
      : []),
    {
      id: 'state',
      header: 'Status',
      cell: (row) => <Tag tone={ORDER_STATE_TONE[row.state]}>{titleCase(row.state)}</Tag>,
    },
    {
      id: 'time',
      header: options.range === 'today' ? 'Time' : 'Date',
      numeric: true,
      cell: (row) => timeOrDate(row.occurredAt, options.range),
    },
  ];
}

export function timeOrDate(iso: string, range: RangeKey): string {
  const date = new Date(iso);
  return range === 'today'
    ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
